'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import StarRating from '@/components/ui/StarRating';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import BookingModal from './BookingModal';

const maids = [
{
  id: 'maid-001',
  name: 'Priya Sharma',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf6664dd-1772918585536.png",
  alt: 'Professional woman with dark hair smiling warmly in white uniform',
  rating: 4.9,
  reviews: 127,
  experience: '6 years',
  status: 'available' as const,
  specialties: ['Deep Cleaning', 'Laundry', 'Cooking'],
  rate: 28,
  rateUnit: '/hour',
  languages: ['English', 'Hindi'],
  verified: true
},
{
  id: 'maid-002',
  name: 'Maria Santos',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_115d61d2c-1765988781101.png",
  alt: 'Smiling woman with curly hair in professional attire',
  rating: 4.7,
  reviews: 89,
  experience: '4 years',
  status: 'available' as const,
  specialties: ['Child Care', 'Meal Prep', 'Organizing'],
  rate: 25,
  rateUnit: '/hour',
  languages: ['English', 'Spanish'],
  verified: true
},
{
  id: 'maid-003',
  name: 'Fatima Al-Rashid',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dce1c59b-1763296539991.png",
  alt: 'Professional woman with head covering smiling confidently',
  rating: 4.8,
  reviews: 203,
  experience: '8 years',
  status: 'booked' as const,
  specialties: ['Deep Cleaning', 'Ironing', 'Elder Care'],
  rate: 32,
  rateUnit: '/hour',
  languages: ['English', 'Arabic'],
  verified: true
},
{
  id: 'maid-004',
  name: 'Nguyen Thi Lan',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_101d5c1e5-1763296283361.png",
  alt: 'Asian woman with straight black hair smiling in casual professional wear',
  rating: 4.6,
  reviews: 64,
  experience: '3 years',
  status: 'available' as const,
  specialties: ['Window Cleaning', 'Kitchen Deep Clean', 'Laundry'],
  rate: 22,
  rateUnit: '/hour',
  languages: ['English', 'Vietnamese'],
  verified: false
},
{
  id: 'maid-005',
  name: 'Grace Okonkwo',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d43dfc4c-1772296838517.png",
  alt: 'African woman with natural hair smiling warmly in professional setting',
  rating: 4.9,
  reviews: 156,
  experience: '7 years',
  status: 'available' as const,
  specialties: ['Full House Cleaning', 'Baby Care', 'Meal Prep'],
  rate: 30,
  rateUnit: '/hour',
  languages: ['English', 'Igbo'],
  verified: true
},
{
  id: 'maid-006',
  name: 'Ananya Krishnan',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17882af93-1763297503073.png",
  alt: 'South Asian woman with long hair in neat professional attire smiling',
  rating: 4.5,
  reviews: 41,
  experience: '2 years',
  status: 'available' as const,
  specialties: ['Cleaning', 'Organizing', 'Pet Care'],
  rate: 20,
  rateUnit: '/hour',
  languages: ['English', 'Tamil'],
  verified: false
}];


export default function MaidCards() {
  const [bookingTarget, setBookingTarget] = useState<(typeof maids)[0] | null>(null);
  const [sortBy, setSortBy] = useState('rating');
  const [filterAvailable, setFilterAvailable] = useState(false);

  const filtered = maids.
  filter((m) => !filterAvailable || m.status === 'available').
  sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'rate-low') return a.rate - b.rate;
    if (sortBy === 'rate-high') return b.rate - a.rate;
    if (sortBy === 'experience') return parseInt(b.experience) - parseInt(a.experience);
    return 0;
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground font-body">
            <span className="font-semibold text-foreground">{filtered.length}</span> maids found
          </p>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={filterAvailable}
              onChange={(e) => setFilterAvailable(e.target.checked)}
              className="w-4 h-4 accent-primary rounded" />

            Available only
          </label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            suppressHydrationWarning
            className="px-3 py-1.5 border border-border rounded-lg text-sm text-foreground bg-white outline-none cursor-pointer">

            <option value="rating">Top Rated</option>
            <option value="rate-low">Price: Low to High</option>
            <option value="rate-high">Price: High to Low</option>
            <option value="experience">Most Experienced</option>
          </select>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
        {filtered.map((maid) =>
        <div
          key={maid.id}
          className="bg-white rounded-xl border border-border shadow-card hover:shadow-card-hover transition-all duration-200 overflow-hidden group">

            {/* Image */}
            <div className="relative h-52 overflow-hidden">
              <AppImage
              src={maid.image}
              alt={maid.alt}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />

              <div className="absolute top-3 left-3">
                <StatusBadge status={maid.status} size="sm" />
              </div>
              {maid.verified &&
            <div className="absolute top-3 right-3 bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Icon name="CheckBadgeIcon" size={12} />
                  Verified
                </div>
            }
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-1">
                <h3 className="font-display font-semibold text-base text-foreground">{maid.name}</h3>
                <div className="text-right">
                  <span className="font-display font-bold text-primary text-base tabular-nums">${maid.rate}</span>
                  <span className="text-xs text-muted-foreground">{maid.rateUnit}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <StarRating value={maid.rating} size={13} />
                <span className="text-xs text-muted-foreground">
                  {maid.rating} ({maid.reviews} reviews)
                </span>
              </div>

              <div className="flex items-center gap-1.5 mb-3 text-xs text-muted-foreground">
                <Icon name="BriefcaseIcon" size={13} />
                {maid.experience} experience
                <span className="mx-1">·</span>
                <Icon name="LanguageIcon" size={13} />
                {maid.languages.join(', ')}
              </div>

              <div className="flex flex-wrap gap-1 mb-4">
                {maid.specialties.map((s) =>
              <span key={`${maid.id}-spec-${s}`} className="text-xs bg-primary/8 text-primary px-2 py-0.5 rounded-full font-medium">
                    {s}
                  </span>
              )}
              </div>

              <button
              onClick={() => setBookingTarget(maid)}
              disabled={maid.status === 'booked'}
              className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95 ${
              maid.status === 'booked' ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-primary text-white hover:bg-primary-light'}`
              }>

                {maid.status === 'booked' ? 'Currently Unavailable' : 'Book Now'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Booking modal */}
      {bookingTarget &&
      <BookingModal
        open={!!bookingTarget}
        onClose={() => setBookingTarget(null)}
        serviceType="maid"
        serviceName={bookingTarget.name}
        serviceRate={bookingTarget.rate}
        serviceRateUnit={bookingTarget.rateUnit}
        serviceImage={bookingTarget.image}
        serviceImageAlt={bookingTarget.alt} />

      }
    </div>);

}